// src/analytics/useScrollDepth.ts
//
// Fires a `scroll_depth` event once per threshold (25/50/75/100%) per page load.
// Throttled via requestAnimationFrame so it never janks scrolling.
//
// Usage: call useScrollDepth() once, high in the tree (e.g. in App or the
// landing page component).

import { useEffect, useRef } from "react";
import { analytics, WebAnalyticsEvent } from "./analyticsService";

const THRESHOLDS = [25, 50, 75, 100] as const;

export const useScrollDepth = (): void => {
  const fired = useRef<Set<number>>(new Set());
  const ticking = useRef(false);

  useEffect(() => {
    const measure = () => {
      ticking.current = false;
      const doc = document.documentElement;
      const scrollable = doc.scrollHeight - window.innerHeight;
      if (scrollable <= 0) return;

      const pct = (window.scrollY / scrollable) * 100;
      for (const t of THRESHOLDS) {
        if (pct >= t && !fired.current.has(t)) {
          fired.current.add(t);
          analytics.logEvent(WebAnalyticsEvent.SCROLL_DEPTH, { percent: t });
        }
      }
    };

    const onScroll = () => {
      if (ticking.current) return;
      ticking.current = true;
      requestAnimationFrame(measure);
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    measure(); // catch short pages already fully visible on load
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
};
